import type { GlAccountOption } from "~/components/select/useGlAccounts";
import type { JoinedGlAccountFragment } from "~/gql/queries/mergeDevIntegration.generated";

import {
	AccountingProvider,
	MergeDevAccountStatus,
	OutboundDetailLevel,
	type SetAccountingIntegrationConfigInput,
	type SetAccountingLineAccountsInput,
	WsapiImportEntityType
} from "~/gql/graphqlBase.generated";
import { type GetAccountingIntegrationConfigQuery } from "~/gql/queries/mergeDevIntegration.generated";
import formatGlAccountLabel from "~/helpers/formatGlAccountLabel";
import { stringFormatDate } from "~/helpers/timestampFormat";

export type JoinedGlAccountFields = JoinedGlAccountFragment;

export type LineAccountField = keyof SetAccountingLineAccountsInput;

export type ListedLineAccountField =
	| "depositAppliedAccountId"
	| "freightAccountId"
	| "roundingAdjustmentAccountId"
	| "salesTaxAccountId";

export interface InvoiceDetailOption {
	helperTextKey: string;
	label: string;
	value: OutboundDetailLevel;
}

const INVOICE_DETAIL_OPTIONS: InvoiceDetailOption[] = [
	{
		helperTextKey: "accountingInvoiceDetail.summaryHelper",
		label: "Summary",
		value: OutboundDetailLevel.SummaryOnly
	},
	{
		helperTextKey: "accountingInvoiceDetail.itemizedHelper",
		label: "Itemized",
		value: OutboundDetailLevel.ItemLevelDetail
	}
];

const ITEMIZED_GL_WARNING =
	"Itemized export posts a separate invoice line for each item on the invoice, to the first account that is set: the customer type's revenue GL account, which routes the whole invoice on either detail level; then the item's own income (GL) account; then its category's income GL account; then the Default income account on the Data Flows tab. Lines with none of those still export, and your accounting system applies its own default. You can map an item's account on its Item edit screen, and a category's or customer type's account in the GL account mapping section on the Data Flows tab.";

const INVOICE_DETAIL_FUTURE_ONLY_NOTE =
	"This applies to invoices exported from now on. It does not change invoices already sent to your accounting system.";

const PROVIDER_LABELS: Record<AccountingProvider, string> = {
	[AccountingProvider.NetSuite]: "NetSuite",
	[AccountingProvider.None]: "your accounting system",
	[AccountingProvider.QuickBooks]: "QuickBooks",
	[AccountingProvider.Xero]: "Xero"
};

const accountingProviderLabel = (provider: AccountingProvider): string => PROVIDER_LABELS[provider];

export interface CurrentAccountingConfig {
	defaultIncomeAccountImportId?: string | null;
	defaultIncomeAccountName?: string | null;
	defaultIncomeAccountRemoteId?: string | null;
	mergeDevAccountId?: string | null;
	provider: AccountingProvider;
}

const buildSetAccountingConfigInput = (
	config: CurrentAccountingConfig,
	outboundDetail: OutboundDetailLevel
): SetAccountingIntegrationConfigInput => ({
	defaultIncomeAccountImportId: config.defaultIncomeAccountImportId ?? null,
	defaultIncomeAccountName: config.defaultIncomeAccountName ?? null,
	defaultIncomeAccountRemoteId: config.defaultIncomeAccountRemoteId ?? null,
	mergeDevAccountId: config.mergeDevAccountId ?? null,
	outboundDetail,
	provider: config.provider
});

export interface DefaultIncomeAccountSelection {
	defaultIncomeAccountImportId: string | null;
	defaultIncomeAccountName: string | null;
	defaultIncomeAccountRemoteId: string | null;
}

// Falls back to the joined account's importId for a config still on the legacy RemoteId, which the server resolves until backfilled.
const defaultIncomeAccountSelectValue = (accountImportId?: string | null, joinedImportId?: string | null): string =>
	accountImportId || joinedImportId || "";

const defaultIncomeAccountFromSelection = (
	accounts: GlAccountOption[],
	importId: string
): DefaultIncomeAccountSelection => {
	const account = accounts.find((candidate) => candidate.id === importId);
	return {
		defaultIncomeAccountImportId: account?.id ?? null,
		defaultIncomeAccountName: account?.name ?? null,
		defaultIncomeAccountRemoteId: account?.remoteId ?? null
	};
};

const DEFAULT_OUTBOUND_DETAIL = OutboundDetailLevel.SummaryOnly;

export interface MergeConnectSetupSelections {
	defaultIncomeAccountName?: string | null;
	defaultIncomeAccountRemoteId?: string | null;
	enabledImportFlows: WsapiImportEntityType[];
	outboundDetail?: OutboundDetailLevel;
}

export interface MergeConnectSetupInputShape {
	defaultIncomeAccountName?: string | null;
	defaultIncomeAccountRemoteId?: string | null;
	enabledImportFlows?: WsapiImportEntityType[] | null;
	outboundDetail: OutboundDetailLevel;
}

const defaultMergeConnectSetup = (): MergeConnectSetupSelections => ({
	defaultIncomeAccountName: null,
	defaultIncomeAccountRemoteId: null,
	enabledImportFlows: [],
	outboundDetail: undefined
});

const buildMergeConnectSetupInput = (selections: MergeConnectSetupSelections): MergeConnectSetupInputShape => {
	const isItemized = selections.outboundDetail === OutboundDetailLevel.ItemLevelDetail;
	return {
		defaultIncomeAccountName: isItemized ? (selections.defaultIncomeAccountName ?? null) : null,
		defaultIncomeAccountRemoteId: isItemized ? (selections.defaultIncomeAccountRemoteId ?? null) : null,
		enabledImportFlows: selections.enabledImportFlows,
		outboundDetail: selections.outboundDetail ?? DEFAULT_OUTBOUND_DETAIL
	};
};

type LiveAccountingConfig = NonNullable<GetAccountingIntegrationConfigQuery["accountingIntegrationConfig"]>;

export type PlaceholderRefusalFields = LiveAccountingConfig["placeholderRefusals"][number];

const mergeConnectSetupFromLiveConfig = (
	config: LiveAccountingConfig | undefined,
	importedFlows: WsapiImportEntityType[]
): MergeConnectSetupSelections => {
	if (!config) {
		return defaultMergeConnectSetup();
	}
	return {
		defaultIncomeAccountName: config.defaultIncomeAccount?.accountName ?? null,
		defaultIncomeAccountRemoteId: config.defaultIncomeAccount?.accountRemoteId ?? null,
		enabledImportFlows: importedFlows,
		outboundDetail: config.outboundDetail
	};
};

export interface InitialImportStatusInput {
	oneTimePullCompleted: boolean;
	oneTimePullCompletedAt?: string | null;
}

export interface InitialImportStatus {
	completed: boolean;
	label: string;
}

const initialImportStatus = (config: InitialImportStatusInput): InitialImportStatus => {
	if (!config.oneTimePullCompleted) {
		return { completed: false, label: "Pending" };
	}
	if (config.oneTimePullCompletedAt) {
		return { completed: true, label: `Completed ${stringFormatDate(config.oneTimePullCompletedAt)}` };
	}
	return { completed: true, label: "Completed" };
};

export interface CustomerTypeGlRoutingNode {
	revenueGlAccount?: { status: MergeDevAccountStatus } | null;
	revenueGlAccountId: string;
}

const isCustomerTypeMapped = (node: CustomerTypeGlRoutingNode): boolean => node.revenueGlAccountId !== "";

const isStaleCustomerTypeMapping = (node: CustomerTypeGlRoutingNode): boolean =>
	isCustomerTypeMapped(node) &&
	(!node.revenueGlAccount || node.revenueGlAccount.status !== MergeDevAccountStatus.Active);

export interface CategoryIncomeGlAccountNode {
	incomeGlAccount?: { accountNumber: string; name: string; status: MergeDevAccountStatus } | null;
	incomeGlAccountId?: null | string;
}

const isStaleCategoryIncomeMapping = (node: CategoryIncomeGlAccountNode): boolean =>
	!!node.incomeGlAccountId && (!node.incomeGlAccount || node.incomeGlAccount.status !== MergeDevAccountStatus.Active);

export interface LineAccountMappingNode {
	accountId?: null | string;
	joinedAccount?: { status: MergeDevAccountStatus } | null;
}

const isStaleLineAccountMapping = (node: LineAccountMappingNode): boolean =>
	!!node.accountId && (!node.joinedAccount || node.joinedAccount.status !== MergeDevAccountStatus.Active);

const LINE_ACCOUNT_FIELDS_IN_DISPLAY_ORDER: readonly ListedLineAccountField[] = [
	"salesTaxAccountId",
	"freightAccountId",
	"depositAppliedAccountId",
	"roundingAdjustmentAccountId"
];

const unsetLineAccountFields = (
	accountIds: Partial<Record<LineAccountField, null | string>>
): ListedLineAccountField[] => LINE_ACCOUNT_FIELDS_IN_DISPLAY_ORDER.filter((field) => !accountIds[field]);

const accountingConfig = {
	accountingProviderLabel,
	buildMergeConnectSetupInput,
	buildSetAccountingConfigInput,
	DEFAULT_OUTBOUND_DETAIL,
	defaultIncomeAccountFromSelection,
	defaultIncomeAccountSelectValue,
	defaultMergeConnectSetup,
	formatJoinedGlAccountLabel: formatGlAccountLabel,
	initialImportStatus,
	INVOICE_DETAIL_FUTURE_ONLY_NOTE,
	INVOICE_DETAIL_OPTIONS,
	isStaleCategoryIncomeMapping,
	isStaleCustomerTypeMapping,
	isStaleLineAccountMapping,
	ITEMIZED_GL_WARNING,
	mergeConnectSetupFromLiveConfig,
	unsetLineAccountFields
};

export default accountingConfig;
